import { useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import Select from "react-select";
import { toast } from "react-toastify";
import {
    Button,
    Col,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
} from "reactstrap";

import useOrganizationFunctionMutations from "../../../../hooks/useOrganizationFunctionMutations";
import useAssignableUsers from "../../hooks/useAssignableUsers";
import AssignedUserCard from "../assigned-user-card";

const MembersAssignment = ({ t, organizationFunction }) => {
    const assignedUsers = Array.isArray(organizationFunction?.assigned_users)
        ? organizationFunction.assigned_users
        : [];

    const [selectedUser, setSelectedUser] = useState(null);
    const [userToRemove, setUserToRemove] = useState(null);

    const { options, isLoading: isUsersLoading } = useAssignableUsers(t);
    const { assignMutation, unassignMutation } = useOrganizationFunctionMutations({
        functionKey: organizationFunction?.key,
        t,
    });

    const assignedIds = useMemo(
        () => new Set(assignedUsers.map((user) => user.id)),
        [assignedUsers]
    );

    const availableOptions = useMemo(
        () => options.filter((option) => !assignedIds.has(option.value)),
        [assignedIds, options]
    );

    const handleAddUser = (option) => {
        setSelectedUser(option);

        if (!option?.value) {
            return;
        }

        assignMutation.mutate(option.value, {
            onSuccess: () => {
                toast(t("User assigned successfully."), {
                    type: "success",
                });
                setSelectedUser(null);
            },
        });
    };

    const handleRemove = () => {
        if (!userToRemove?.id) {
            return;
        }

        unassignMutation.mutate(userToRemove.id, {
            onSuccess: () => {
                setUserToRemove(null);
            },
        });
    };

    return (
        <div>
            <Row className="mb-3">
                <Col sm="12">
                    <Label className="form-label">
                        {`${t("Add User")}:`}
                    </Label>
                    <Select
                        classNamePrefix="select2-selection"
                        isDisabled={isUsersLoading || assignMutation.isLoading}
                        isLoading={isUsersLoading || assignMutation.isLoading}
                        menuPortalTarget={document.body}
                        onChange={handleAddUser}
                        options={availableOptions}
                        placeholder={t("Search and select a user...")}
                        styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        value={selectedUser}
                    />
                </Col>
            </Row>

            {assignedUsers.length ? (
                <div className="d-flex flex-column gap-3">
                    {assignedUsers.map((user) => (
                        <AssignedUserCard
                            key={user.id}
                            user={user}
                            actionsPlacement="below"
                            actions={
                                <Button
                                    color="danger"
                                    outline
                                    size="sm"
                                    type="button"
                                    onClick={() => setUserToRemove(user)}
                                >
                                    {t("Remove")}
                                </Button>
                            }
                        />
                    ))}
                </div>
            ) : (
                <p className="text-center text-muted mt-4 mb-0">
                    <strong>{t("No user assigned")}</strong>
                </p>
            )}

            <Modal isOpen={!!userToRemove} backdrop="static">
                <ModalHeader toggle={() => setUserToRemove(null)}>
                    {t("Remove")}
                </ModalHeader>
                <ModalBody>
                    {t("Are you sure you want to remove this user from the Function?")}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        type="button"
                        onClick={() => setUserToRemove(null)}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        color="danger"
                        type="button"
                        disabled={unassignMutation.isLoading}
                        onClick={handleRemove}
                    >
                        {unassignMutation.isLoading ? (
                            <Spinner className="me-2" size="sm" />
                        ) : null}
                        {t("Remove")}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default withNamespaces()(MembersAssignment);
